import React, { useState } from "react";
import Select from "react-select";
import {
  CreditCard,
  History,
  Calendar,
  DollarSign,
  ChevronDown,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Receipt,
} from "lucide-react";

import TableSchedulePayments from "blocks/table-scheduled-payments/table-scheduled-payments";
import TablePaymentHistory from "blocks/table-payment-history/table-payment-history";
import TableAccountAdjustments from "blocks/table-account-adjustments/table-account-adjustments";
import API from "../../utils/api";
import Skeleton from "react-loading-skeleton";

const customStyles = {
  container: (provided) => ({
    ...provided,
    width: "100%",
    maxWidth: "100%",
  }),
  control: (provided, state) => ({
    ...provided,
    border: state.isFocused ? "2px solid #3b82f6" : "2px solid #4b5563",
    borderRadius: "12px",
    width: "100%",
    backgroundColor: "rgba(31, 41, 55, 0.9)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
    "&:hover": {
      borderColor: "#3b82f6",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    maxWidth: "100%",
    overflow: "hidden",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontWeight: "500",
    maxWidth: "100%",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
    backgroundColor: "#1f2937",
    border: "1px solid #374151",
    overflow: "hidden",
    zIndex: 10000,
    width: "100%",
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 10000,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#3b82f6"
      : state.isFocused
        ? "#374151"
        : "#1f2937",
    color: state.isSelected ? "white" : "#e5e7eb",
    cursor: "pointer",
    margin: "0",
    "&:active": {
      backgroundColor: "#3b82f6",
    },
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#9ca3af",
    "&:hover": {
      color: "#e5e7eb",
    },
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    backgroundColor: "#4b5563",
  }),
};

const PaymentsPage = () => {
  const [scheduled, setScheduled] = useState(null);
  const [payHistory, setPayHistory] = useState(null);
  const [adjustments, setAdjustments] = useState(null);
  const [lastPull, setLastPull] = useState("");
  const [accountOptions, setAccountOptions] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountsLoaded, setAccountsLoaded] = useState(false);

  const fetchScheduled = async (accountId) => {
    if (!accountId) return;
    try {
      const response = await API.get(
        `/accounts/${accountId}/scheduled-payments`
      );
      if (response.data.scheduled) {
        setScheduled(response.data.scheduled);
      } else {
        setScheduled([]);
      }
    } catch (e) {
      console.log(e);
      setScheduled([]);
    }
  };

  const fetchHistory = async (accountId) => {
    if (!accountId) return;
    try {
      const response = await API.get(`/accounts/${accountId}/payment-history`);
      if (response.data.history) {
        setPayHistory(response.data.history);
      } else {
        setPayHistory([]);
      }
      if (response.data.last_pull) {
        setLastPull(response.data.last_pull);
      }
    } catch (e) {
      console.log(e);
      setPayHistory([]);
    }
  };

  const fetchAdjustments = async (accountId) => {
    if (!accountId) return;
    try {
      const response = await API.get(`/accounts/${accountId}/adjustments`);
      if (!response.data.result_code && response.data.adjustments) {
        setAdjustments(response.data.adjustments);
      } else {
        setAdjustments([]);
      }
    } catch (e) {
      console.log(e);
      setAdjustments([]);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await API.get("/accounts");
      if (!response.data.result_code && response.data.account) {
        const accountList = [];
        const activeAccounts = response.data.account.filter(
          (account) => account.status === 0 || account.status === undefined
        );

        activeAccounts.forEach((account) => {
          const accountName = account.chargeoff_creditor?.name || account.creditor?.name || 'Unknown Account';
          const refId = account.chargeoff_creditor?.ref_id || account.creditor?.ref_id || account.id;
          accountList.push({
            value: account.id,
            label:
              accountName + " #" + refId + " (Balance: $" + account.balance_current + ")",
            balance: account.balance_current,
          });
        });

        setAccountOptions(accountList);

        // Auto-select first account if available
        if (accountList.length > 0) {
          setSelectedAccount(accountList[0]);
          setAccountsLoaded(true);
          return accountList[0].value;
        }
      }
      setAccountsLoaded(true);
      return null;
    } catch (e) {
      console.log(e);
      setAccountsLoaded(true);
      return null;
    }
  };

  const afterDelete = async () => {
    if (selectedAccount) {
      await fetchHistory(selectedAccount.value);
      await fetchAdjustments(selectedAccount.value);
      await fetchScheduled(selectedAccount.value);
    }
  };

  const handleAccountChange = async (selected) => {
    setSelectedAccount(selected);
    // Clear current data
    setScheduled(null);
    setPayHistory(null);
    setAdjustments(null);
    // Fetch new data for selected account
    if (selected) {
      await fetchHistory(selected.value);
      await fetchAdjustments(selected.value);
      await fetchScheduled(selected.value);
    }
  };

  // On focus of this tab, fetch accounts first, then payment data
  React.useEffect(() => {
    const initializeData = async () => {
      const firstAccountId = await fetchAccounts();
      if (firstAccountId) {
        await fetchHistory(firstAccountId);
        await fetchAdjustments(firstAccountId);
        await fetchScheduled(firstAccountId);
      }
    };
    initializeData();
  }, []);

  return (
    <div className="component-wrapper">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Payments</h1>
          <p className="text-gray-300">
            Manage your payment history and scheduled transactions
          </p>
        </div>

        {/* Account Selection Card */}
        <div className="bg-gray-800/50 backdrop-blur rounded-2xl border border-gray-700 p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-500 rounded-xl flex items-center justify-center">
              <CreditCard size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">
                Select Account
              </h2>
              <p className="text-gray-400 text-sm">
                Choose an account to view payment details
              </p>
            </div>
          </div>

          <Select
            id="select-account"
            placeholder="Select an account..."
            value={selectedAccount}
            options={accountOptions}
            styles={customStyles}
            onChange={handleAccountChange}
            isDisabled={!accountsLoaded || accountOptions.length === 0}
            isClearable={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            menuShouldScrollIntoView={true}
          />
        </div>

        {/* Scheduled Payments Section */}
        <div className="bg-gray-800/50 backdrop-blur rounded-2xl border border-gray-700 p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Calendar size={20} className="text-green-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white">
                Scheduled Payments
              </h2>
            </div>
          </div>

          <div className="relative">
            {scheduled === null && (
              <div className="py-2">
                <TableSchedulePayments
                  data={[]}
                  isLoading={true}
                  accountId={selectedAccount?.value}
                />
              </div>
            )}
            {scheduled?.length === 0 && (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar size={32} className="text-gray-500" />
                </div>
                <p className="text-gray-300 text-lg">No scheduled payments</p>
                <p className="text-gray-500 text-sm mt-2">
                  Schedule automatic payments to never miss a due date
                </p>
              </div>
            )}
            {scheduled?.length > 0 && (
              <div className="overflow-x-auto">
                <TableSchedulePayments
                  data={scheduled}
                  afterDelete={afterDelete}
                  accountId={selectedAccount?.value}
                />
              </div>
            )}
          </div>
        </div>

        {/* Account Adjustments Section - Only show if adjustments exist */}
        {adjustments?.length > 0 && (
          <div className="bg-gray-800/50 backdrop-blur rounded-2xl border border-gray-700 p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Receipt size={20} className="text-purple-400" />
                </div>
                <h2 className="text-2xl font-semibold text-white">
                  Account Adjustments
                </h2>
              </div>
            </div>

            <div className="overflow-x-auto">
              <TableAccountAdjustments data={adjustments} />
            </div>
          </div>
        )}

        {/* Payment History Section */}
        <div className="bg-gray-800/50 backdrop-blur rounded-2xl border border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <History size={20} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white">
                Payment History
              </h2>
            </div>
            {lastPull && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Clock size={14} />
                <span>Updated: {new Date(lastPull).toLocaleString()}</span>
              </div>
            )}
          </div>

          <div className="relative">
            {payHistory === null && (
              <div className="py-2">
                <TablePaymentHistory data={[]} isLoading={true} />
              </div>
            )}
            {payHistory?.length === 0 && (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign size={32} className="text-gray-500" />
                </div>
                <p className="text-gray-300 text-lg">
                  No payment history available
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  Payments will appear here once processed
                </p>
              </div>
            )}
            {payHistory?.length > 0 && (
              <div className="overflow-x-auto">
                <TablePaymentHistory data={payHistory} />
              </div>
            )}
          </div>

          {(lastPull || payHistory?.length > 0) && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-xs text-gray-400">
                Note: Payment history does not reflect processing fees that may
                have been charged separately.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentsPage;
